/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { platformMcpDismissOnboarding } from "../funcs/platformMcpDismissOnboarding.js";
import { platformMcpDistributeOnboardingCandidate } from "../funcs/platformMcpDistributeOnboardingCandidate.js";
import { platformMcpGetOnboarding } from "../funcs/platformMcpGetOnboarding.js";
import { platformMcpRecheckOnboardingReadiness } from "../funcs/platformMcpRecheckOnboardingReadiness.js";
import { platformMcpRecordAgentConfigurationCopied } from "../funcs/platformMcpRecordAgentConfigurationCopied.js";
import { platformMcpRecordDashboardCtaEvent } from "../funcs/platformMcpRecordDashboardCtaEvent.js";
import { platformMcpRecordInstallIntent } from "../funcs/platformMcpRecordInstallIntent.js";
import { platformMcpRemoveOnboardingDistribution } from "../funcs/platformMcpRemoveOnboardingDistribution.js";
import { platformMcpRepairOnboardingPublication } from "../funcs/platformMcpRepairOnboardingPublication.js";
import { platformMcpStartOnboarding } from "../funcs/platformMcpStartOnboarding.js";
import { platformMcpStartOnboardingSetup } from "../funcs/platformMcpStartOnboardingSetup.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { PlatformMCPOnboardingSetupHandoff } from "../models/components/platformmcponboardingsetuphandoff.js";
import { PlatformMCPOnboardingState } from "../models/components/platformmcponboardingstate.js";
import {
  DismissPlatformMCPOnboardingRequest,
  DismissPlatformMCPOnboardingSecurity,
} from "../models/operations/dismissplatformmcponboarding.js";
import {
  DistributePlatformMCPOnboardingCandidateRequest,
  DistributePlatformMCPOnboardingCandidateSecurity,
} from "../models/operations/distributeplatformmcponboardingcandidate.js";
import {
  GetPlatformMCPOnboardingRequest,
  GetPlatformMCPOnboardingSecurity,
} from "../models/operations/getplatformmcponboarding.js";
import {
  RecheckPlatformMCPOnboardingReadinessRequest,
  RecheckPlatformMCPOnboardingReadinessSecurity,
} from "../models/operations/recheckplatformmcponboardingreadiness.js";
import {
  RecordPlatformMCPAgentConfigurationCopiedRequest,
  RecordPlatformMCPAgentConfigurationCopiedSecurity,
} from "../models/operations/recordplatformmcpagentconfigurationcopied.js";
import {
  RecordPlatformMCPDashboardCtaEventRequest,
  RecordPlatformMCPDashboardCtaEventSecurity,
} from "../models/operations/recordplatformmcpdashboardctaevent.js";
import {
  RecordPlatformMCPInstallIntentRequest,
  RecordPlatformMCPInstallIntentSecurity,
} from "../models/operations/recordplatformmcpinstallintent.js";
import {
  RemovePlatformMCPOnboardingDistributionRequest,
  RemovePlatformMCPOnboardingDistributionSecurity,
} from "../models/operations/removeplatformmcponboardingdistribution.js";
import {
  RepairPlatformMCPOnboardingPublicationRequest,
  RepairPlatformMCPOnboardingPublicationSecurity,
} from "../models/operations/repairplatformmcponboardingpublication.js";
import {
  StartPlatformMCPOnboardingRequest,
  StartPlatformMCPOnboardingSecurity,
} from "../models/operations/startplatformmcponboarding.js";
import {
  StartPlatformMCPOnboardingSetupRequest,
  StartPlatformMCPOnboardingSetupSecurity,
} from "../models/operations/startplatformmcponboardingsetup.js";
import { unwrapAsync } from "../types/fp.js";

export class PlatformMcp extends ClientSDK {
  /**
   * dismissOnboarding platformMcp
   *
   * @remarks
   * Dismiss the optional current user's Platform MCP onboarding workflow without changing organization setup or project resources.
   */
  async dismissOnboarding(
    request?: DismissPlatformMCPOnboardingRequest | undefined,
    security?: DismissPlatformMCPOnboardingSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(platformMcpDismissOnboarding(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * distributeOnboardingCandidate platformMcp
   *
   * @remarks
   * Attach the workflow-bound ready local MCP to the selected project's existing Default plugin. The caller supplies only the selected project slug and its opaque server-issued version token.
   */
  async distributeOnboardingCandidate(
    request: DistributePlatformMCPOnboardingCandidateRequest,
    security?: DistributePlatformMCPOnboardingCandidateSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpDistributeOnboardingCandidate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getOnboarding platformMcp
   *
   * @remarks
   * Get the current user's safe Platform MCP onboarding projection for the active organization.
   */
  async getOnboarding(
    request?: GetPlatformMCPOnboardingRequest | undefined,
    security?: GetPlatformMCPOnboardingSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpGetOnboarding(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recheckOnboardingReadiness platformMcp
   *
   * @remarks
   * Force a rate-limited authenticated readiness recheck for the workflow-bound local registration.
   */
  async recheckOnboardingReadiness(
    request?: RecheckPlatformMCPOnboardingReadinessRequest | undefined,
    security?: RecheckPlatformMCPOnboardingReadinessSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpRecheckOnboardingReadiness(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recordAgentConfigurationCopied platformMcp
   *
   * @remarks
   * Record that the user copied the displayed Platform MCP configuration or completed an equivalent supported agent-setup action.
   */
  async recordAgentConfigurationCopied(
    request?: RecordPlatformMCPAgentConfigurationCopiedRequest | undefined,
    security?: RecordPlatformMCPAgentConfigurationCopiedSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpRecordAgentConfigurationCopied(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recordDashboardCtaEvent platformMcp
   *
   * @remarks
   * Record a bounded Platform MCP dashboard CTA impression, selection, or dismissal.
   */
  async recordDashboardCtaEvent(
    request: RecordPlatformMCPDashboardCtaEventRequest,
    security?: RecordPlatformMCPDashboardCtaEventSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(platformMcpRecordDashboardCtaEvent(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recordInstallIntent platformMcp
   *
   * @remarks
   * Record a selected manual-install client family for the current user's Platform MCP workflow.
   */
  async recordInstallIntent(
    request: RecordPlatformMCPInstallIntentRequest,
    security?: RecordPlatformMCPInstallIntentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpRecordInstallIntent(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * removeOnboardingDistribution platformMcp
   *
   * @remarks
   * Remove only the workflow-bound MCP from the selected project's existing Default plugin. Registration, readiness, connection, and prior evidence remain intact.
   */
  async removeOnboardingDistribution(
    request: RemovePlatformMCPOnboardingDistributionRequest,
    security?: RemovePlatformMCPOnboardingDistributionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpRemoveOnboardingDistribution(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * repairOnboardingPublication platformMcp
   *
   * @remarks
   * Retry in-memory local package publication for the workflow-bound distribution without changing its attachment or version.
   */
  async repairOnboardingPublication(
    request: RepairPlatformMCPOnboardingPublicationRequest,
    security?: RepairPlatformMCPOnboardingPublicationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpRepairOnboardingPublication(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * startOnboarding platformMcp
   *
   * @remarks
   * Create or resume the current user's durable Platform MCP onboarding workflow.
   */
  async startOnboarding(
    request: StartPlatformMCPOnboardingRequest,
    security?: StartPlatformMCPOnboardingSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingState> {
    return unwrapAsync(platformMcpStartOnboarding(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * startOnboardingSetup platformMcp
   *
   * @remarks
   * Return the secure setup continuation for the workflow-bound registration. Browser Catalogue registrations return their existing same-origin dashboard Inspect page; the local fixture returns a one-time handoff for its synthetic provider setup endpoint.
   */
  async startOnboardingSetup(
    request?: StartPlatformMCPOnboardingSetupRequest | undefined,
    security?: StartPlatformMCPOnboardingSetupSecurity | undefined,
    options?: RequestOptions,
  ): Promise<PlatformMCPOnboardingSetupHandoff> {
    return unwrapAsync(platformMcpStartOnboardingSetup(
      this,
      request,
      security,
      options,
    ));
  }
}
